import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  useQueries,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  QueryFunctionContext,
  Query,
} from "@tanstack/react-query";
import { ApiClientError } from "../api";

type Params = Record<string, string | number | boolean | undefined> | null;

type RequestBody = Record<string, unknown> | unknown[] | void;

type ClientForQueries = {
  get: (url: string, params?: Params, signal?: AbortSignal) => Promise<unknown>;
  getAll: (
    url: string,
    params?: Params,
    signal?: AbortSignal,
  ) => Promise<unknown>;
  head: (
    url: string,
    params?: Params,
    signal?: AbortSignal,
  ) => Promise<unknown>;
};

type ClientForMutations = {
  post: (url: string, body?: RequestBody) => Promise<unknown>;
  patch: (url: string, body?: RequestBody) => Promise<unknown>;
  delete: (url: string, body?: RequestBody) => Promise<unknown>;
  upload: (url: string, body?: RequestBody) => Promise<unknown>;
};

type ApiClient = ClientForQueries & ClientForMutations;

type MutationVariables =
  | (RequestBody & { mutationUrl?: never; mutationBody?: never })
  | { mutationUrl: string; mutationBody: RequestBody }
  | void;

const createQueryKey = (url: string, scope: string, params?: Params) =>
  [url, params ?? {}, { scope }] as const;

type CustomQueryKey = ReturnType<typeof createQueryKey>;

export type CustomUseQueryOptions<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TError extends ApiClientError = ApiClientError,
> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, CustomQueryKey>,
  "queryKey" | "queryFn"
>;

/**
 * Custom hook for querying the Netilion API. It uses \@tanstack/react-query under the hood.
 * @returns The API object.
 */
export const useApiWithClient = (client: ApiClient, scope: string) => {
  const queryClient = useQueryClient();

  const createMutation = (method: keyof ClientForMutations) => ({
    /**
     * @param url - The URL to send the request to.
     * @param options - Additional options for the mutation.
     * @returns The the mutation object.
     */
    useMutation: <
      TData = unknown,
      TVariables extends MutationVariables = MutationVariables,
      TError = ApiClientError,
      TContext = unknown,
    >(
      mutationUrl?: string | null,
      options?: Omit<
        UseMutationOptions<TData, TError, TVariables, TContext>,
        "mutationFn"
      >,
    ) =>
      useMutation<TData, TError, TVariables, TContext>({
        mutationFn: (variables) => {
          const url = variables?.mutationUrl ?? mutationUrl;
          // istanbul ignore next
          if (!url) {
            throw new Error(
              "mutationUrl must be passed via mutate(variables.mutationUrl) or as the first argument to useMutation",
            );
          }
          const mutationBody = variables?.mutationBody ?? variables;
          return client[method](url, mutationBody) as Promise<TData>;
        },
        onSettled: (_, __, variables) => {
          const predicate = (query: Query) => {
            const queryKey = query.queryKey as CustomQueryKey;
            return (
              queryKey[0] === (variables?.mutationUrl ?? mutationUrl) &&
              queryKey[2]?.scope === scope
            );
          };
          if (method === "delete") {
            return queryClient.removeQueries({
              predicate,
            });
          }
          return queryClient.invalidateQueries({
            predicate,
          });
        },
        ...options,
      }),
  });

  const createQuery = (method: keyof ClientForQueries) => ({
    /**
     * @param url - The URL to send the request to.
     * @param params - URL query params as object.
     * @param options - Additional options for the query.
     * @returns The the query object.
     */
    useQuery: <
      TQueryFnData = unknown,
      TData = TQueryFnData,
      TError extends ApiClientError = ApiClientError,
    >(
      url: string,
      params?: Params,
      options?: Omit<
        UseQueryOptions<TQueryFnData, TError, TData, CustomQueryKey>,
        "queryKey" | "queryFn"
      >,
    ) =>
      useQuery<TQueryFnData, TError, TData, CustomQueryKey>({
        queryFn: ({ signal }) =>
          client[method](url, params, signal) as Promise<TQueryFnData>,
        queryKey: createQueryKey(url, scope, params),
        ...options,
      }),
  });

  type PaginatedResponse = {
    pagination: {
      next?: string | null;
      page: number;
    };
  };

  const createInfiniteQuery = (method: keyof ClientForQueries) => ({
    /**
     * @param url - The URL to send the request to.
     * @param params - URL query params as object.
     * @param options - Additional options for the query.
     * @returns The the query object.
     */
    useInfiniteQuery: <
      TQueryFnData extends PaginatedResponse,
      TData = TQueryFnData,
      TError extends ApiClientError = ApiClientError,
    >(
      url: string,
      params?: Params,
      options?: Omit<
        UseInfiniteQueryOptions<
          TQueryFnData,
          TError,
          TData,
          TQueryFnData,
          CustomQueryKey
        >,
        "queryKey" | "queryFn" | "getNextPageParam"
      >,
    ) =>
      useInfiniteQuery<TQueryFnData, TError, TData, CustomQueryKey>({
        queryKey: createQueryKey(url, scope, params),
        queryFn: ({
          signal,
          pageParam = 1,
        }: QueryFunctionContext<CustomQueryKey, number>) =>
          client[method](
            url,
            { page: pageParam, ...params },
            signal,
          ) as Promise<TQueryFnData>,
        getNextPageParam: (lastPage) =>
          lastPage.pagination.next ? lastPage.pagination.page + 1 : undefined,
        ...options,
      }),
  });

  type QueriesOption = {
    url: string;
    params: Params;
    options: Omit<UseQueryOptions, "queryKey" | "queryFn">;
  };

  type QueriesOptions = QueriesOption[];

  const createQueries = (method: keyof ClientForQueries) => ({
    /**
     * Custom hook for making multiple queries at once.
     *
     * @param queries - An array of query objects. Each query object should have the following properties:
     * @param queries[].url - The URL to send the request to.
     * @param queries[].params - URL query params as an object.
     * @param queries[].options - Additional options for the query.
     *
     * @returns An array of query objects.
     *
     * @example
     * const results = useQueries([
     *   { url: '/assets', params: { include: 'status' }, options: { enabled: true } },
     *   { url: '/assets', params: { include: 'specifications' }, options: { enabled: false } },
     * ]);
     */
    useQueries: (queries: QueriesOptions) =>
      useQueries({
        queries: queries.map((query) => ({
          queryKey: createQueryKey(query.url, scope, query.params),
          queryFn: ({ signal }: QueryFunctionContext) =>
            client[method](query.url, query.params, signal),
          ...query.options,
        })),
      }),
  });

  const api = {
    get: {
      ...createQuery("get"),
      ...createInfiniteQuery("get"),
      ...createQueries("get"),
    },
    getAll: {
      ...createQuery("getAll"),
      ...createQueries("getAll"),
    },
    head: { ...createQuery("head"), ...createQueries("head") },
    post: createMutation("post"),
    patch: createMutation("patch"),
    delete: createMutation("delete"),
    upload: createMutation("upload"),
  };

  return api;
};
